import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Save, Trash2, Printer, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import PositionenTabelle from '../components/PositionenTabelle';
import DruckbogenPanel from '../components/DruckbogenPanel';
import { BASIS, type Auftrag, type BogenOptionen, type Einstellungen } from '../lib/typen';
import { abweichungen, druckansichtHtml, leerePosition, nummeriere, ohneInitialen, zaehleJeGroesse, zuKurzeInitialen } from '../lib/liste';
import { fehlerText } from '../lib/dateien';

const LEER: Auftrag = { titel: '', artikel: '', hinweise: '', positionen: [leerePosition(1)], kontrollsummen: [] };

export default function EditorPage({ base }: { base: string }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [auftrag, setAuftrag] = useState<Auftrag | null>(null);
  const [einstellungen, setEinstellungen] = useState<Einstellungen | null>(null);
  const [optionen, setOptionen] = useState<BogenOptionen | null>(null);
  const [geaendert, setGeaendert] = useState(false);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const e: Einstellungen = (await api.get(`${BASIS}/einstellungen`)).data.data;
        setEinstellungen(e);
        let a: Auftrag;
        if (id) {
          a = (await api.get(`${BASIS}/auftraege/${id}`)).data.data;
          setGeaendert(false);
        } else {
          const entwurf = (location.state as { entwurf?: Auftrag } | null)?.entwurf;
          a = entwurf ? { ...entwurf, positionen: nummeriere(entwurf.positionen) } : LEER;
          setGeaendert(!!entwurf);
        }
        setAuftrag(a);
        setOptionen({ ...e.defaults, ...(a.bogenOptionen ?? {}) });
      } catch (err) {
        toast.error(await fehlerText(err, 'Auftrag konnte nicht geladen werden'));
        navigate(base);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Ungespeicherte Änderungen nicht still verlieren (Tab schließen / Reload)
  useEffect(() => {
    if (!geaendert) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [geaendert]);

  if (!auftrag || !einstellungen || !optionen) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Lädt …</div>;
  }

  const aendere = (patch: Partial<Auftrag>) => { setAuftrag({ ...auftrag, ...patch }); setGeaendert(true); };

  async function speichern(extra: Partial<Auftrag> & { gedruckt?: boolean } = {}) {
    if (!auftrag!.titel.trim()) { toast.error('Bitte einen Titel eintragen (z. B. Vereinsname).'); return; }
    setSpeichert(true);
    try {
      const body = { ...auftrag, ...extra, bogenOptionen: optionen };
      const r = id
        ? await api.put(`${BASIS}/auftraege/${id}`, body)
        : await api.post(`${BASIS}/auftraege`, body);
      const gespeichert: Auftrag = r.data.data;
      setAuftrag(gespeichert);
      setGeaendert(false);
      toast.success('Gespeichert');
      if (!id) navigate(`${base}/auftrag/${gespeichert.id}`, { replace: true });
    } catch (e) {
      toast.error(await fehlerText(e, 'Speichern fehlgeschlagen'));
    } finally {
      setSpeichert(false);
    }
  }

  async function loeschen() {
    if (!id || !confirm(`Auftrag „${auftrag!.titel}“ wirklich löschen?`)) return;
    try {
      await api.delete(`${BASIS}/auftraege/${id}`);
      toast.success('Gelöscht');
      navigate(base);
    } catch (e) {
      toast.error(await fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  }

  function liste_drucken() {
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup blockiert — bitte Popups für Libero erlauben.'); return; }
    w.document.write(druckansichtHtml(auftrag!));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  }

  const summe = zaehleJeGroesse(auftrag.positionen);
  const gesamt = summe.reduce((s, k) => s + k.anzahl, 0);
  const diff = abweichungen(auftrag.positionen, auftrag.kontrollsummen);
  const ohne = ohneInitialen(auftrag.positionen);
  const zuKurz = zuKurzeInitialen(auftrag.positionen);
  const unsicher = auftrag.positionen.filter((p) => p.unsicher).length;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => (!geaendert || confirm('Ungespeicherte Änderungen verwerfen?')) && navigate(base)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">{id ? auftrag.titel || 'Auftrag' : 'Neue Liste'}</h1>
          {geaendert && <span className="rounded bg-[#E9FF57] px-2 py-0.5 text-xs font-medium text-[#1A1A1A]">ungespeichert</span>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={liste_drucken}><Printer className="mr-1 h-4 w-4" /> Liste drucken</Button>
          {id && <Button variant="outline" onClick={loeschen}><Trash2 className="mr-1 h-4 w-4" /> Löschen</Button>}
          <Button onClick={() => speichern()} disabled={speichert} className="bg-[#1A1A1A] text-white hover:bg-[#333]">
            {speichert ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Speichern
          </Button>
        </div>
      </div>

      <div className="grid gap-3 rounded-lg border border-[#DAD9D9] bg-white p-4 md:grid-cols-3">
        <label className="text-xs text-gray-600">
          Titel / Verein
          <input value={auftrag.titel} onChange={(e) => aendere({ titel: e.target.value })}
            className="mt-1 w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A]" placeholder="z. B. SG Eggolsheim-Hallerndorf" />
        </label>
        <label className="text-xs text-gray-600">
          Artikel
          <input value={auftrag.artikel} onChange={(e) => aendere({ artikel: e.target.value })}
            className="mt-1 w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A]" placeholder="z. B. Jako Winterjacke" />
        </label>
        <label className="text-xs text-gray-600">
          Hinweise des Kunden
          <input value={auftrag.hinweise} onChange={(e) => aendere({ hinweise: e.target.value })}
            className="mt-1 w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A]" placeholder="Farbe, Position, …" />
        </label>
        <div className="flex flex-wrap items-center gap-2 md:col-span-3">
          <span className="text-sm font-semibold text-[#1A1A1A]">{gesamt} Stück:</span>
          {summe.map((k) => (
            <span key={k.groesse} className="rounded bg-[#DAD9D9]/60 px-2 py-0.5 text-sm">
              {k.groesse === 'OHNE' ? 'ohne Größe' : k.groesse}: <b>{k.anzahl}</b>
            </span>
          ))}
          {auftrag.quelleDateiname && <span className="ml-auto text-xs text-gray-400">Quelle: {auftrag.quelleDateiname}</span>}
          {auftrag.gedrucktAm && (
            <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">gedruckt {new Date(auftrag.gedrucktAm).toLocaleDateString('de-DE')}</span>
          )}
        </div>
      </div>

      {(diff.length > 0 || ohne.length > 0 || zuKurz.length > 0 || unsicher > 0) && (
        <div className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unsicher > 0 && <p className="flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {unsicher} Zeile(n) unsicher gelesen — gelb markiert, bitte mit dem Zettel vergleichen.</p>}
          {diff.map((d) => (
            <p key={d.groesse} className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> Größe {d.groesse}: Liste {d.liste} Stk, Kunde hat {d.kunde} notiert.
            </p>
          ))}
          {zuKurz.length > 0 && <p className="flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Initialen brauchen 2–3 Zeichen: {zuKurz.map((p) => `#${p.nr}`).join(', ')}</p>}
          {ohne.length > 0 && <p className="flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Ohne Initialen (fehlen auf dem Bogen): {ohne.map((p) => `#${p.nr}`).join(', ')}</p>}
        </div>
      )}

      <PositionenTabelle positionen={auftrag.positionen} onChange={(positionen) => aendere({ positionen })} />

      <DruckbogenPanel
        titel={auftrag.titel}
        positionen={auftrag.positionen}
        einstellungen={einstellungen}
        optionen={optionen}
        onOptionen={(o) => { setOptionen(o); setGeaendert(true); }}
        onHeruntergeladen={() => {
          if (id && !auftrag.gedrucktAm) speichern({ gedruckt: true });
        }}
      />
    </>
  );
}
